import { Lock, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/Tooltip';

interface SidebarItemProps {
  icon: LucideIcon;
  label: string;
  to: string;
  collapsed: boolean;
  badge?: number;
  onClick?: () => void;
  /** When set, active highlight is determined by whether this path starts with
   *  `to` rather than by the current URL. Used to preserve the highlighted
   *  module when navigating across feature boundaries (e.g. patient → chart). */
  activeOverridePath?: string;
  /** CAB-604: the user's plan doesn't include this module. Shows a lock, but
   *  stays navigable on purpose — the destination explains what is locked and
   *  how to unlock it. Removing the item instead would leave a nav gap the user
   *  can't reason about. */
  locked?: boolean;
}

export function SidebarItem({
  icon,
  label,
  to,
  collapsed,
  badge,
  onClick,
  activeOverridePath,
  locked = false,
}: SidebarItemProps) {
  const Icon = icon;

  const isOverrideActive =
    activeOverridePath !== undefined && activeOverridePath !== ''
      ? activeOverridePath.startsWith(to)
      : false;

  // If onClick is provided, render a button instead of a NavLink
  if (onClick) {
    const buttonContent = (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white',
          collapsed && 'justify-center px-2',
        )}
      >
        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{label}</span>
            {badge !== undefined && badge > 0 && (
              <span className="bg-action flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                {badge}
              </span>
            )}
          </>
        )}
        {collapsed && badge !== undefined && badge > 0 && (
          <span className="bg-action absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white">
            {badge}
          </span>
        )}
      </button>
    );

    if (collapsed) {
      return (
        <div className="relative">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
            <TooltipContent side="right">{label}</TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return buttonContent;
  }

  if (collapsed) {
    return (
      <div className="relative">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white',
                  (activeOverridePath !== undefined && activeOverridePath !== ''
                    ? isOverrideActive
                    : isActive) && 'bg-white/10 text-white',
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {badge !== undefined && badge > 0 && (
                <span className="bg-action absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white">
                  {badge}
                </span>
              )}
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right">
            {label}
            {badge !== undefined && badge > 0 && ` (${String(badge)})`}
            {locked && ' — available on Pro'}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) => {
        const active =
          activeOverridePath !== undefined && activeOverridePath !== ''
            ? isOverrideActive
            : isActive;
        return cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          active
            ? 'border-l-3 border-[var(--wf-blue)] bg-white/10 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        );
      }}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {locked && (
        <Lock className="h-3.5 w-3.5 shrink-0 text-white/50" aria-label="Available on Pro" />
      )}
      {badge !== undefined && badge > 0 && (
        <span className="bg-action flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
          {badge}
        </span>
      )}
    </NavLink>
  );
}
